import {
  ButtonGeneral,
  ButtonPrimary,
  ButtonSecondary,
  Dropdown,
  DropdownWidth,
  Input,
  typographyRem,
} from '@by-miles/bymiles-styles'
import { postcodeRegex } from '@by-miles/regex'
import React, { useContext, useEffect, useState } from 'react'

import config from '../../config'
import { AddressInput } from '../../generated/graphql'
import { ProviderContext } from '../../Provider'
import { fetcher } from '../../utils/axios'
import { ToolTip } from '../ToolTip'
import { FindMyAddressStyle } from './FindMyAddressStyle'
const { h5: H5, textLink: TextLink } = typographyRem

interface FindMyAddressProps {
  setAddress: (address: AddressInput) => void
  confirm?: () => void
  manualEdit?: () => void
  cancel?: () => void
  showConfirmButtons?: boolean
  showCantFindAddressLink?: boolean
  showEditButton?: boolean
  dataCy?: string
  preFilledError?: string
  toopTip?: string
}

interface AddressOption {
  value: AddressInput
  label: string
}

export const FindMyAddress = ({
  setAddress,
  confirm,
  manualEdit,
  cancel,
  showConfirmButtons = true,
  showCantFindAddressLink = true,
  dataCy = 'address',
  preFilledError,
  toopTip,
}: FindMyAddressProps) => {
  const { state } = useContext(ProviderContext)
  const [postcode, setPostcode] = useState('')
  const [validPostcode, setValidPostcode] = useState(false)
  const [addresses, setAddresses] = useState<AddressOption[]>([])
  const [selectedAddress, setSelectedAddress] = useState<AddressInput>()
  const [loading, setLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState(preFilledError || '')
  const [error, setError] = useState(false)

  useEffect(() => {
    if (preFilledError) {
      setErrorMessage(preFilledError)
    }
  }, [])

  const formatLine = (line: string | undefined | null) => {
    return line ? `${line}, ` : ''
  }

  const handleFindAddressClick = async () => {
    setLoading(true)
    await fetcher(`${config.quoteFlowAPIOrigin}/v1/address/${postcode.toUpperCase()}`, 'GET', null)
      .then(response => {
        setErrorMessage('')
        if (response?.data?.length > 0) {
          const addresses: AddressOption[] = response.data.map((address: AddressInput) => ({
            value: {
              line_1: address.line_1 || '',
              line_2: address.line_2 || '',
              line_3: address.line_3 || '',
              city: address.city || '',
              postcode: postcode.toUpperCase().replace(/\s+/g, '') || '',
              country: address.country || '',
              county: address.county || '',
            },
            label: `${formatLine(address.line_1)}${formatLine(address.line_2)}${formatLine(address.line_3)}${
              address.city
            }`,
          }))
          setAddresses(addresses)
        }
      })
      .catch(e => {
        setErrorMessage("Sorry, we can't find an address with these details. Please check they’re correct.")
      })
    setLoading(false)
  }

  const onChange = (value: string) => {
    setError(!value.match(postcodeRegex))
    if (value.match(postcodeRegex)) {
      setValidPostcode(true)
      setError(false)
      setErrorMessage('')
    } else {
      if (preFilledError) {
        setError(false)
        if (errorMessage !== preFilledError) {
          setErrorMessage(preFilledError)
        }
      }
    }
    setPostcode(value)
  }

  const handleConfirm = () => {
    if (postcode && validPostcode && selectedAddress) {
      confirm && confirm()
    } else {
      setError(false)
      setErrorMessage(
        "Sorry, you need to enter a complete address before you can continue. Don't forget to select an address from the list below after searching for your postcode",
      )
    }
  }

  return (
    <FindMyAddressStyle data-cy={`${dataCy}-postcode-input-text-field`}>
      {showConfirmButtons && <H5 className="where-to-title">Where would like us to send it?</H5>}
      <div className="find-address-wrapper">
        <div className="input-info-wrapper">
          <Input
            name="find-address"
            className="find-address-input"
            placeHolder="Postcode"
            onChange={value => onChange(value.toUpperCase())}
            value={postcode}
            error={errorMessage}
            errorNoMessage={error}
            dataCy={`${dataCy}-postcode-input-field`}
            autoComplete="postal-code"
          />
          {toopTip && <ToolTip text={toopTip} className="tool-tip-mobile" />}
        </div>
        <ButtonGeneral
          type="button"
          className="find-address-button"
          label="Find Address"
          size="small"
          loading={loading}
          disabled={!validPostcode}
          onClick={handleFindAddressClick}
          dataCy={`${dataCy}-find-address-button`}
        />
        {toopTip && <ToolTip text={toopTip} className="tool-tip-desktop" dataCy={`${dataCy}-info-button`} />}
      </div>
      <div className="address-selector-wrapper">
        {addresses?.length > 0 && (
          <Dropdown
            handleDropdownChange={(option: AddressOption) => {
              setSelectedAddress(option.value)
              setAddress(option.value)
            }}
            menuPosition="relative"
            options={addresses}
            width={DropdownWidth.Large}
            placeholder="Select address"
            isMobile={state.isMobile}
            dataCy={`${dataCy}-dropdown-list`}
          />
        )}
      </div>

      {addresses?.length > 0 && showCantFindAddressLink && manualEdit && (
        <div className="manual-link">
          <TextLink onClick={manualEdit} data-cy={`${dataCy}-no-address-button`}>
            Can’t find your address?
          </TextLink>
        </div>
      )}

      {showConfirmButtons && (
        <>
          <ButtonPrimary
            type="button"
            className="confirm LoNotSensitive"
            label="Confirm"
            onClick={handleConfirm}
            shadow
            dataCy={`${dataCy}-confirm-address-button`}
          />
          <ButtonSecondary
            type="button"
            className="cancel-postcode LoNotSensitive"
            label="Cancel"
            onClick={cancel}
            dataCy={`${dataCy}-edit-address-button`}
          />
        </>
      )}
    </FindMyAddressStyle>
  )
}
